import React, { useEffect, useMemo, useState } from "react";
import { pushFirebase, updateUserInRoomList, changeRoomStatus } from "./firebaseCRUD";
import { useSelector, useDispatch } from "react-redux";
import { checkIfRoomIsClosed } from "./ChatScripts";
import {
   setIsChatMinimized,
   setChatSettings,
   setIsChatOpen,
} from "../../../../appRedux/actions/ChatSettings";
import { v4 as uuidv4 } from "uuid";
import { Button, Message } from "../../../BP2Components";
import { useIntl } from "react-intl";
import IntlMessages from "../../../../util/IntlMessages";
import firebase from "./FirebaseConfig";
import { useChat } from "../../../../context/ChatContext/ChatContext";

const CreateChat = ({
   currentWidget,
   rowData,
   chatTitleKey,
   columns,
   isChatButtonVisible,
   chatType,
   onChatCreate,
}) => {
   const [visible, setVisible] = useState(false);
   const [modeInfo, setModeInfo] = useState(null);

   const { chatData } = useChat();

   let buttonTranslationFromBoardTranslations;

   let caseReference =
      rowData?.api_rowMetaData?.recordId ||
      rowData?._sysRecordId ||
      rowData?.["id"] ||
      rowData?.["ID"];

   let privateChatTitle = rowData?.[currentWidget?.privateChatTitle]?.value
      ? rowData[currentWidget?.privateChatTitle]?.value
      : rowData?.[currentWidget?.privateChatTitle];

   let publicChatTitle = rowData?.[currentWidget?.publicChatTitle]?.value
      ? rowData[currentWidget?.publicChatTitle]?.value
      : rowData?.[currentWidget?.publicChatTitle];

   const { isChatMinimized } = useSelector(({ chatSettings }) => chatSettings);
   const { chatSettings } = useSelector(({ chatSettings }) => chatSettings);
   const { locale } = useSelector(({ settings }) => settings);
   const { currentBoard } = useSelector(({ boards }) => boards);
   const { id, oldId } = useSelector(({ user }) => user);

   const intl = useIntl();
   const dispatch = useDispatch();

   const formattedPrivateChatTitle = privateChatTitle
      ? `${caseReference} | ${privateChatTitle}`
      : caseReference;

   const formattedPublicChatTitle = publicChatTitle
      ? `${intl.formatMessage({ id: "chat.publicRoomLabel" })} ${caseReference} | ${publicChatTitle}`
      : `${intl.formatMessage({ id: "chat.publicRoomLabel" })} ${caseReference}`;

   // Get users from chatUsersColumnTechNames
   const chatUsersFromWidget =
      currentWidget?.chatUsersColumnTechNames
         ?.map((user) => rowData?.[user.columnTechName])
         .filter(Boolean) || [];

   // Find owner from users with type "owner"
   const ownerUser = currentWidget?.chatUsersColumnTechNames?.find((user) => user.type === "owner");
   const ownerRaw = ownerUser ? rowData?.[ownerUser.columnTechName] : undefined;
   const ownerParsed = Number.parseInt(ownerRaw, 10);
   const roomOwner = Number.isFinite(ownerParsed) ? ownerParsed : oldId;

   const roomId = uuidv4();

   useEffect(() => {
      setModeInfo(null);
   }, []);

   const chooseMode = (mode) => {
      setModeInfo(mode);
      setVisible(false);
   };

   const closeSelectModal = () => {
      setVisible(false);
   };

   const openSelectModal = () => {
      setVisible(true);
   };

   const openChatModal = (room) => {
      dispatch(setIsChatOpen(true));

      dispatch(
         setChatSettings({
            ...chatSettings,
            currentRoom: room,
            selectedRoomId: room.id,
            selectedRoomKey: room.key,
            selectedRoomTitle: room.title,
         }),
      );

      if (isChatMinimized) {
         dispatch(setIsChatMinimized());
      }

      if (onChatCreate) {
         onChatCreate();
      }
   };

   const createPrivateChat = async () => {
      const currentUserId = oldId;

      if (!caseReference) {
         Message.warning(intl.formatMessage({ id: "chat.noCaseReference" }));
         return;
      }

      const existingRoomWithUser = chatData?.privateRooms?.find((room) => {
         return (
            room.caseReference === caseReference &&
            room.type === "private" &&
            room.status === "active" &&
            room.chatUsers?.includes(currentUserId)
         );
      });

      if (existingRoomWithUser) {
         console.log("Found existing room for user:", existingRoomWithUser);
         openChatModal(existingRoomWithUser);
         return;
      }

      const closedRoom = await checkIfRoomIsClosed(chatData, undefined, caseReference);

      if (closedRoom && closedRoom.type === "private") {
         const shouldReactivate =
            closedRoom.owner === currentUserId || closedRoom.chatUsers?.includes(currentUserId);

         if (shouldReactivate) {
            console.log("Reactivating closed room:", closedRoom);
            await changeRoomStatus("rooms", closedRoom.key, "active");

            let updatedUsers = [...(closedRoom.chatUsers || [])];
            if (!updatedUsers.includes(currentUserId)) {
               updatedUsers.push(currentUserId);
            }

            if (chatUsersFromWidget.length > 0) {
               chatUsersFromWidget.forEach((userId) => {
                  const userIdNum = Number.parseInt(userId, 10);
                  if (Number.isFinite(userIdNum) && !updatedUsers.includes(userIdNum)) {
                     updatedUsers.push(userIdNum);
                  }
               });
            }

            await updateUserInRoomList("rooms", closedRoom.key, [...new Set(updatedUsers)]);
            openChatModal({ ...closedRoom, chatUsers: updatedUsers, status: "active" });
            return;
         }
      }

      try {
         const snapshot = await firebase
            .database()
            .ref("rooms")
            .orderByChild("caseReference")
            .equalTo(caseReference)
            .once("value");

         const firebaseRooms = snapshot.val();

         if (firebaseRooms) {
            const existingRoomKey = Object.keys(firebaseRooms).find((key) => {
               const room = firebaseRooms[key];
               return (
                  room.type === "private" &&
                  room.status === "active" &&
                  room.chatUsers?.includes(currentUserId)
               );
            });

            if (existingRoomKey) {
               console.log("Found existing room in Firebase:", firebaseRooms[existingRoomKey]);
               openChatModal({ ...firebaseRooms[existingRoomKey], key: existingRoomKey });
               return;
            }
         }
      } catch (error) {
         console.error("Error checking Firebase for existing rooms:", error);
      }

      console.log("Creating new private room for case:", caseReference);

      let chatUsers = [currentUserId];

      if (roomOwner && roomOwner !== currentUserId) {
         chatUsers.push(roomOwner);
      }

      if (chatUsersFromWidget.length > 0) {
         chatUsersFromWidget.forEach((userId) => {
            const userIdNum = Number.parseInt(userId, 10);
            if (Number.isFinite(userIdNum) && !chatUsers.includes(userIdNum)) {
               chatUsers.push(userIdNum);
            }
         });
      }

      const uniqueUsers = [...new Set(chatUsers)];

      const newRoom = pushFirebase("rooms");
      const roomData = {
         id: roomId,
         title: formattedPrivateChatTitle,
         subTitle: publicChatTitle || "",
         caseReference: caseReference,
         owner: roomOwner || currentUserId,
         type: "private",
         status: "active",
         seenBy: [currentUserId],
         chatUsers: uniqueUsers,
         chatMessage: [],
         listId: currentWidget?.listWidgetSeries?.[0]?.listId || currentWidget?.listId,
         createdAt: new Date().toISOString(),
         createdBy: currentUserId,
      };

      await newRoom.set(roomData);
      console.log("Created new room:", roomData);
      openChatModal({ ...roomData, key: newRoom.key });
   };

   const createPublicChat = async () => {
      const publicCaseReference = `public#${caseReference}`;
      let existingPublicRoom = null;

      if (chatData?.allRooms) {
         existingPublicRoom = chatData.allRooms.find(
            (room) => room.caseReference === publicCaseReference && !room.owner,
         );
      }

      if (!existingPublicRoom) {
         try {
            const snapshot = await firebase.database().ref("rooms").once("value");
            const allRooms = snapshot.val();

            if (allRooms) {
               const roomsArray = Object.entries(allRooms).map(([key, room]) => ({
                  key,
                  ...(room || {}),
               }));

               existingPublicRoom = roomsArray.find(
                  (room) =>
                     room.caseReference === publicCaseReference &&
                     room.type === "public" &&
                     room.status === "active",
               );
            }
         } catch (error) {
            console.error("Error searching Firebase for public rooms:", error);
         }
      }

      // check for archived/closed
      if (!existingPublicRoom) {
         try {
            const closedRoom = await checkIfRoomIsClosed(chatData, undefined, publicCaseReference);
            if (closedRoom && !closedRoom?.owner) {
               await changeRoomStatus("rooms", closedRoom.key, "active");
               const updatedUsers = [...new Set([...(closedRoom.chatUsers || []), oldId])];
               await updateUserInRoomList("rooms", closedRoom.key, updatedUsers);
               openChatModal({ ...closedRoom, chatUsers: updatedUsers });
               return;
            }
         } catch (error) {
            console.error("Error checking archived rooms:", error);
         }
      }

      if (existingPublicRoom) {
         let updatedUsers = existingPublicRoom.chatUsers || [];
         if (!updatedUsers.includes(oldId)) {
            updatedUsers = [...updatedUsers, oldId];
            await updateUserInRoomList("rooms", existingPublicRoom.key, updatedUsers);
         }
         openChatModal({ ...existingPublicRoom, chatUsers: updatedUsers });
         return;
      }

      let usersArray = [oldId];

      // For now we are not using this - there are more problems with the users being added to firebase in public rooms
      //
      // if (auctionOwnerId) {
      //    const auctionOwnerIdNum = parseInt(auctionOwnerId);
      //    if (auctionOwnerIdNum && !usersArray.includes(auctionOwnerIdNum)) {
      //       usersArray.push(auctionOwnerIdNum);
      //    }
      // }
      const sortedUsersArray = [...new Set(usersArray)].filter((user) => user !== undefined);

      const newRoom = pushFirebase("rooms");
      const roomData = {
         id: roomId,
         title: formattedPublicChatTitle,
         subTitle: publicChatTitle || "",
         caseReference: publicCaseReference,
         type: "public",
         status: "active",
         seenBy: [oldId],
         chatUsers: sortedUsersArray,
         chatMessage: [],
         listId: currentWidget?.listWidgetSeries?.[0]?.listId || currentWidget?.listId,
      };

      newRoom
         .set(roomData)
         .then(() => {
            const roomWithKey = { ...roomData, key: newRoom.key };
            openChatModal(roomWithKey);
         })
         .catch((error) => {
            console.error("Error creating room:", error);
         });
   };

   const renderChatButton = () => {
      if (isChatButtonVisible === true) {
         if (currentBoard?.translations) {
            const settingsTranslations = currentBoard.translations.find(
               (translObj) => translObj?.key === chatTitleKey,
            );
            if (settingsTranslations && settingsTranslations.values[locale]) {
               buttonTranslationFromBoardTranslations = settingsTranslations.values[locale];
            }
         }

         if (currentWidget?.buttonsInSelectMode) {
            return (
               <Button
                  buttonType="primary"
                  onClick={(e) =>
                     chatType === "privateChat" ? createPrivateChat() : createPublicChat()
                  }
               >
                  {buttonTranslationFromBoardTranslations ? (
                     buttonTranslationFromBoardTranslations
                  ) : (
                     <IntlMessages id={chatTitleKey} />
                  )}
               </Button>
            );
         } else {
            return (
               <p
                  onClick={(e) =>
                     chatType === "privateChat" ? createPrivateChat() : createPublicChat()
                  }
                  style={{ margin: 0 }}
                  className={`dropdown-button-item`}
               >
                  {buttonTranslationFromBoardTranslations ? (
                     buttonTranslationFromBoardTranslations
                  ) : (
                     <IntlMessages id={chatTitleKey} />
                  )}
               </p>
            );
         }
      }
   };

   useMemo(() => {
      if (modeInfo === "user") {
         // REMOVED: No longer using owner-based direct room creation
         console.warn("Direct room creation disabled");
         setModeInfo(null);
      } else if (modeInfo === "group") {
         createPrivateChat();
      }
      setModeInfo(null);
   }, [modeInfo]);

   return <>{renderChatButton()}</>;
};

export default CreateChat;
